import type { CompanyUpdate, FinancialItem, InvestmentCompany } from '../types';
import { summarizeCompanyUpdate } from '../services/companyUpdateSummarizer';

function iso(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString();
}

export const seedFinancialItems: FinancialItem[] = [
  {
    id: 'fin-seed-1',
    name: 'Liquid cash',
    type: 'asset',
    domain: 'personal',
    category: 'cash',
    currentValue: 240000,
    monthlyChange: 1200,
    rating: 'excellent',
    createdAt: iso(120),
    updatedAt: iso(2),
  },
  {
    id: 'fin-seed-2',
    name: 'Founder loan',
    type: 'liability',
    domain: 'business',
    category: 'loan',
    currentValue: 85000,
    monthlyChange: -400,
    interestRate: 6.5,
    rating: 'bad',
    createdAt: iso(200),
    updatedAt: iso(5),
  },
  {
    id: 'fin-seed-3',
    name: 'Home equity',
    type: 'asset',
    domain: 'personal',
    category: 'real_estate',
    currentValue: 310000,
    monthlyChange: 0,
    rating: 'good',
    createdAt: iso(400),
    updatedAt: iso(30),
  },
];

export const seedInvestmentCompanies: InvestmentCompany[] = [
  {
    id: 'co-seed-1',
    name: 'Northwind Analytics',
    websiteUrl: 'https://www.notion.so',
    countryCode: 'us',
    foundingYear: 2019,
    industry: 'B2B SaaS',
    ownershipPercentage: 12,
    ticketSize: 480000,
    investmentDate: '2022-03-15T12:00:00.000Z',
    valuationMethod: 'manual',
    manualValuation: 9072000,
    exitProbabilityThisYear: 8,
    expectedDividendThisYear: 15000,
    createdAt: iso(300),
    updatedAt: iso(10),
  },
  {
    id: 'co-seed-2',
    name: 'Blue Harbor Foods',
    websiteUrl: 'https://www.unilever.com',
    countryCode: 'de',
    foundingYear: 2016,
    industry: 'CPG',
    ownershipPercentage: 7.5,
    ticketSize: 350000,
    investmentDate: '2020-09-01T12:00:00.000Z',
    valuationMethod: 'manual',
    manualValuation: 2880000,
    exitProbabilityThisYear: 15,
    expectedDividendThisYear: 8000,
    createdAt: iso(280),
    updatedAt: iso(12),
  },
  {
    id: 'co-seed-3',
    name: 'Lattice Robotics',
    websiteUrl: 'https://www.automation.com',
    foundingYear: 2021,
    industry: 'Industrial automation',
    ownershipPercentage: 4,
    ticketSize: 175000,
    investmentDate: '2024-06-12T12:00:00.000Z',
    valuationMethod: 'manual',
    manualValuation: 4200000,
    exitProbabilityThisYear: 3,
    expectedDividendThisYear: 0,
    createdAt: iso(150),
    updatedAt: iso(8),
  },
  {
    id: 'co-seed-4',
    name: 'Meridian Health AI',
    websiteUrl: 'https://www.epic.com',
    foundingYear: 2020,
    industry: 'Healthcare IT',
    ownershipPercentage: 9,
    ticketSize: 920000,
    investmentDate: '2021-11-05T12:00:00.000Z',
    valuationMethod: 'manual',
    manualValuation: 14520000,
    exitProbabilityThisYear: 12,
    expectedDividendThisYear: 22000,
    createdAt: iso(260),
    updatedAt: iso(4),
  },
  {
    id: 'co-seed-5',
    name: 'Coastal Logistics',
    websiteUrl: 'https://www.dhl.com',
    foundingYear: 2014,
    industry: 'Logistics',
    ownershipPercentage: 3,
    ticketSize: 1100000,
    investmentDate: '2018-02-20T12:00:00.000Z',
    valuationMethod: 'manual',
    manualValuation: 5460000,
    exitProbabilityThisYear: 20,
    expectedDividendThisYear: 35000,
    createdAt: iso(320),
    updatedAt: iso(20),
  },
];

function companyUpdateFromRaw(
  id: string,
  companyId: string,
  dateIso: string,
  rawText: string,
  createdAtIso: string
): CompanyUpdate {
  const s = summarizeCompanyUpdate(rawText);
  return {
    id,
    companyId,
    date: dateIso,
    rawText,
    aiSummary: s.aiSummary,
    sentiment: s.sentiment,
    keyPoints: s.keyPoints,
    createdAt: createdAtIso,
  };
}

export const seedCompanyUpdates: CompanyUpdate[] = [
  companyUpdateFromRaw(
    'upd-seed-1',
    'co-seed-1',
    iso(14),
    'Closed two enterprise pilots. Revenue recognition improved after pricing fix. Team hired a strong VP Sales.\n' +
      'Pipeline grew quarter over quarter.',
    iso(14)
  ),
  companyUpdateFromRaw(
    'upd-seed-2',
    'co-seed-2',
    iso(28),
    'Margins under pressure from ingredient costs. Launched a second SKU line to diversify.\n' +
      'Cash runway comfortable through next year.',
    iso(28)
  ),
];
